import { readXlsxRows } from './xlsx';
import type {
  Earning,
  EarningKind,
  InvestmentAsset,
  InvestmentType,
  Trade,
} from '../types';

/**
 * Importa o relatório de Movimentação da B3 (área do investidor > Extratos >
 * Movimentação), em .xlsx ou .csv.
 *
 * O relatório mistura tudo: compras, vendas, proventos, subscrições, renda
 * fixa e Tesouro. Só ação e FII entram; o resto é listado como ignorado para
 * o usuário ver o que ficou de fora, em vez de sumir em silêncio.
 */

/** JCP é retido a 15% na fonte. */
const JCP_RATE = 0.15;

const COLUMNS = {
  direction: 'Entrada/Saída',
  date: 'Data',
  movement: 'Movimentação',
  product: 'Produto',
  quantity: 'Quantidade',
  unitPrice: 'Preço unitário',
  total: 'Valor da Operação',
} as const;

export class B3ImportError extends Error {}

export interface B3ParsedTrade {
  key: string;
  line: number;
  /**
   * Entrada deduzida de um evento societário (subscrição exercida), porque
   * sem ela a posição ficaria negativa. Entra sem preço: o relatório não traz.
   */
  inferred?: boolean;
  ticker: string;
  productName: string;
  assetType: InvestmentType;
  operation: 'buy' | 'sell';
  date: Date;
  quantity: number;
  unitPrice: number;
  total: number;
  duplicate: boolean;
}

export interface B3ParsedEarning {
  key: string;
  line: number;
  ticker: string;
  productName: string;
  assetType: InvestmentType;
  kind: EarningKind;
  paymentDate: Date;
  quantityBase: number;
  valuePerShare: number;
  grossValue: number;
  irrfWithheld: number;
  duplicate: boolean;
}

/**
 * Linha que mexe na posição mas o relatório não sabe precificar, ou cujo
 * efeito é ambíguo. Nunca é importada sozinha: vai para revisão manual.
 */
export interface B3Event {
  line: number;
  ticker: string;
  productName: string;
  movement: string;
  date: Date;
  quantity: number;
  unitPrice: number;
  total: number;
  /** O que provavelmente aconteceu, em português. */
  explanation: string;
}

export interface B3SkippedRow {
  line: number;
  product: string;
  movement: string;
  reason: string;
}

export interface B3NewAsset {
  ticker: string;
  name: string;
  type: InvestmentType;
}

/** Papel que fica com quantidade negativa — sinal de evento societário faltando. */
export interface B3NegativePosition {
  ticker: string;
  /** Data em que a posição ficou negativa pela primeira vez. */
  date: Date;
  shortfall: number;
}

export interface B3ImportPlan {
  trades: B3ParsedTrade[];
  earnings: B3ParsedEarning[];
  newAssets: B3NewAsset[];
  /** Eventos societários que precisam de decisão sua. */
  events: B3Event[];
  /** Vendas sem cota suficiente: falta alguma entrada no histórico. */
  negativePositions: B3NegativePosition[];
  skipped: B3SkippedRow[];
}

// ============================================
// PARSING DE VALORES
// ============================================

/**
 * O .xlsx traz número com ponto decimal ("17.765"); o .csv da B3 traz no
 * formato brasileiro ("1.776,50"). A vírgula é o que distingue os dois.
 */
export function parseNumber(raw: string): number {
  const text = (raw ?? '').trim();
  if (!text || text === '-') return 0;

  const cleaned = text.replace(/[\u0300-\u036f]/g, '');
  const normalized = cleaned.includes(',')
    ? cleaned.replace(/\./g, '').replace(',', '.')
    : cleaned;

  const value = Number(normalized);
  return Number.isFinite(value) ? value : 0;
}

/** "28/10/2026" no fuso local. */
export function parseBrDate(raw: string): Date | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((raw ?? '').trim());
  if (!match) return null;
  const date = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
}

/** Ticker de ação ou FII: 4 letras + 1 ou 2 dígitos. */
const TICKER_PATTERN = /^[A-Z]{4}\d{1,2}$/;

/** Produtos do Tesouro Direto não têm ticker — o nome é o identificador. */
const TESOURO_PATTERN = /^TESOURO\s/;

/**
 * Identificador estável para um título do Tesouro: o nome sem o prefixo
 * "Tesouro", em maiúsculas. Encurtar mais arriscaria colidir — "IPCA+ 2035"
 * e "IPCA+ com Juros Semestrais 2035" são títulos diferentes.
 */
export function tesouroTicker(product: string): string {
  return normalize(product).replace(TESOURO_PATTERN, '').trim();
}

export function isTesouro(product: string): boolean {
  return TESOURO_PATTERN.test(normalize(product).trim());
}

export function splitProduct(product: string): { ticker: string; name: string } {
  // Tesouro não tem ticker: o nome inteiro identifica o título.
  if (isTesouro(product)) {
    return { ticker: tesouroTicker(product), name: product.trim() };
  }
  // Alguns nomes têm vários " - " ("HGLG11 - PÁTRIA LOG - FDO INV IMOB").
  const separator = product.indexOf(' - ');
  if (separator < 0) return { ticker: product.trim(), name: '' };
  return {
    ticker: product.slice(0, separator).trim().toUpperCase(),
    name: product.slice(separator + 3).trim(),
  };
}

/**
 * Tipo pelo NOME do produto, não pelo final do ticker: BPAC11 e TAEE11
 * terminam em 11 e são units (ação), não FII.
 */
export function detectAssetType(ticker: string, name: string): InvestmentType {
  const text = normalize(`${ticker} ${name}`);
  if (isTesouro(name)) return 'tesouro';
  if (/\bFII\b/.test(text) || /IMOB/.test(text)) return 'fii';
  // Sem nome para julgar, o final 11 é o melhor palpite disponível.
  if (!name && /11$/.test(ticker)) return 'fii';
  return 'acao';
}

/** Direitos de subscrição (final 12/13) não são posição e o app não os modela. */
function isSubscriptionTicker(ticker: string): boolean {
  return /(?:12|13)$/.test(ticker);
}

// ============================================
// MAPEAMENTO DE MOVIMENTAÇÕES
// ============================================

type Mapping =
  | { sort: 'trade'; operation: 'buy' | 'sell' }
  | { sort: 'trade'; operation: 'direction' }
  | { sort: 'earning'; kind: EarningKind | 'byType' }
  | { sort: 'event'; explanation: string }
  | { sort: 'skip'; reason: string };

const MOVEMENTS: { match: RegExp; mapping: Mapping }[] = [
  // Liquidação da bolsa: crédito é compra, débito é venda.
  { match: /^TRANSFERENCIA - LIQUIDACAO$/, mapping: { sort: 'trade', operation: 'direction' } },
  { match: /^COMPRA$/, mapping: { sort: 'trade', operation: 'buy' } },
  { match: /^VENDA$/, mapping: { sort: 'trade', operation: 'sell' } },

  // "- Transferido" vem em par crédito/débito quando a posição muda de
  // corretora: o provento já foi pago antes, não é renda nova.
  { match: /- TRANSFERIDO$/, mapping: { sort: 'skip', reason: 'Provento transferido entre corretoras (não é renda nova)' } },

  { match: /^RENDIMENTO$/, mapping: { sort: 'earning', kind: 'byType' } },
  { match: /^DIVIDENDO$/, mapping: { sort: 'earning', kind: 'dividendo' } },
  { match: /^JUROS SOBRE CAPITAL PROPRIO$/, mapping: { sort: 'earning', kind: 'jcp' } },
  { match: /^AMORTIZACAO/, mapping: { sort: 'earning', kind: 'amortizacao' } },
  { match: /RESTITUICAO DE CAPITAL/, mapping: { sort: 'earning', kind: 'restituicao_capital' } },
  { match: /^BONIFICACAO/, mapping: { sort: 'earning', kind: 'bonificacao' } },

  // Eventos que entregam ou tiram cota sem preço no relatório.
  {
    match: /^ATUALIZACAO$/,
    mapping: {
      sort: 'event',
      explanation:
        'Crédito de evento societário: subscrição exercida que virou cota, incorporação de fundo ou troca de ticker. Às vezes é só um recadastramento da custódia, sem cota nova.',
    },
  },
  {
    match: /SUBSCRICAO - EXERCIDO|DIREITOS DE SUBSCRICAO - EXERCIDO/,
    mapping: {
      sort: 'event',
      explanation:
        'Subscrição exercida. As cotas entram depois, numa linha de Atualização — e o preço pago não vem neste relatório.',
    },
  },
  {
    match: /^RESGATE$/,
    mapping: {
      sort: 'event',
      explanation: 'Resgate/extinção de cotas, normalmente o fim de uma incorporação.',
    },
  },
  {
    match: /LEILAO DE FRACAO/,
    mapping: {
      sort: 'event',
      explanation: 'Fração leiloada: é uma venda, e o custo vem do papel que originou a fração.',
    },
  },
  {
    match: /FRACAO EM ATIVOS/,
    mapping: {
      sort: 'event',
      explanation: 'Fração separada da posição, para ser leiloada depois.',
    },
  },
  { match: /DESDOBRO|GRUPAMENTO|BONIFICACAO EM ATIVOS/, mapping: { sort: 'event', explanation: 'Evento societário que muda a quantidade sem alterar o custo total.' } },

  // Inertes: o relatório lança e desfaz na mesma quantidade.
  { match: /RECIBO DE SUBSCRICAO/, mapping: { sort: 'skip', reason: 'Recibo de subscrição (entra e sai no mesmo dia)' } },
  { match: /SUBSCRICAO|CESSAO DE DIREITOS/, mapping: { sort: 'skip', reason: 'Direito de subscrição' } },
  { match: /^APLICACAO$/, mapping: { sort: 'skip', reason: 'Renda fixa' } },
  { match: /TRANSFERENCIA$/, mapping: { sort: 'skip', reason: 'Transferência entre contas' } },
];

function mapMovement(movement: string): Mapping {
  const text = normalize(movement).trim();
  for (const { match, mapping } of MOVEMENTS) {
    if (match.test(text)) return mapping;
  }
  return { sort: 'skip', reason: `Movimentação não suportada: ${movement}` };
}

// ============================================
// LEITURA DO ARQUIVO
// ============================================

function splitCsvLine(line: string, separator: string): string[] {
  const fields: string[] = [];
  let current = '';
  let quoted = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i]!;
    if (char === '"') {
      if (quoted && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        quoted = !quoted;
      }
    } else if (char === separator && !quoted) {
      fields.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  fields.push(current.trim());
  return fields;
}

function parseCsv(text: string): string[][] {
  const clean = text.replace(/^\ufeff/, '');
  const firstLine = clean.split(/\r?\n/)[0] ?? '';
  // A B3 exporta com ponto e vírgula; arquivos reconvertidos podem vir com vírgula.
  const separator = (firstLine.match(/;/g)?.length ?? 0) >= (firstLine.match(/,/g)?.length ?? 0) ? ';' : ',';

  return clean
    .split(/\r?\n/)
    .filter((line) => line.trim() !== '')
    .map((line) => splitCsvLine(line, separator));
}

/** Lê .xlsx ou .csv e devolve as linhas cruas. */
export async function readB3File(file: File): Promise<string[][]> {
  const isCsv = /\.csv$/i.test(file.name);
  if (isCsv) return parseCsv(await file.text());
  return readXlsxRows(await file.arrayBuffer());
}

// ============================================
// MONTAGEM DO PLANO DE IMPORTAÇÃO
// ============================================

function dateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;
}

function tradeKey(ticker: string, operation: string, date: Date, quantity: number, unitPrice: number) {
  return `${ticker}|${operation}|${dateKey(date)}|${quantity}|${unitPrice.toFixed(6)}`;
}

function earningKey(ticker: string, kind: string, date: Date, grossValue: number) {
  return `${ticker}|${kind}|${dateKey(date)}|${grossValue.toFixed(2)}`;
}

function countBy<T>(items: T[], keyOf: (item: T) => string): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    const key = keyOf(item);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

export interface ExistingData {
  assets: InvestmentAsset[];
  trades: Trade[];
  earnings: Earning[];
}

/**
 * Posição negativa é impossível: se há mais venda que compra, faltou uma
 * entrada que o relatório não traz como compra — quase sempre subscrição
 * exercida, que a B3 credita numa linha de "Atualização" sem preço.
 *
 * Esta função usa o próprio rombo para decidir quais Atualizações são reais:
 * consome as do papel, em ordem cronológica, até a posição parar de ficar
 * negativa. As que não forem necessárias continuam só como evento para
 * revisão — é o que impede inflar PETR4, onde a Atualização é só
 * recadastramento de custódia e a posição nunca falta.
 */
function inferMissingEntries(
  parsed: B3ParsedTrade[],
  events: B3Event[],
  existing: ExistingData
): { inferred: B3ParsedTrade[]; consumed: Set<number> } {
  const tickerById = new Map(
    existing.assets.map((asset) => [asset.id, asset.ticker.toUpperCase()])
  );

  const candidatesByTicker = new Map<string, B3Event[]>();
  for (const event of events) {
    if (event.movement.toUpperCase() !== 'ATUALIZAÇÃO' && normalize(event.movement) !== 'ATUALIZACAO') {
      continue;
    }
    if (event.quantity <= 0) continue;
    const list = candidatesByTicker.get(event.ticker) ?? [];
    list.push(event);
    candidatesByTicker.set(event.ticker, list);
  }
  for (const list of candidatesByTicker.values()) {
    list.sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  interface Movement {
    ticker: string;
    date: Date;
    delta: number;
  }
  const movements: Movement[] = [];
  for (const trade of existing.trades) {
    const ticker = tickerById.get(trade.assetId);
    if (!ticker) continue;
    movements.push({
      ticker,
      date: trade.date,
      delta: trade.operation === 'buy' ? trade.quantity : -trade.quantity,
    });
  }
  for (const trade of parsed) {
    if (trade.duplicate) continue;
    movements.push({
      ticker: trade.ticker,
      date: trade.date,
      delta: trade.operation === 'buy' ? trade.quantity : -trade.quantity,
    });
  }
  movements.sort((a, b) => a.date.getTime() - b.date.getTime());

  const running = new Map<string, number>();
  const used = new Map<string, number>();
  const consumed = new Set<number>();
  const inferred: B3ParsedTrade[] = [];

  for (const movement of movements) {
    let next = (running.get(movement.ticker) ?? 0) + movement.delta;

    // Cobre o rombo com os eventos do papel anteriores a esta data.
    if (next < -1e-8) {
      const candidates = candidatesByTicker.get(movement.ticker) ?? [];
      let index = used.get(movement.ticker) ?? 0;

      while (next < -1e-8 && index < candidates.length) {
        const candidate = candidates[index]!;
        if (candidate.date.getTime() > movement.date.getTime()) break;

        consumed.add(candidate.line);
        inferred.push({
          key: tradeKey('INFERIDO|' + candidate.ticker, 'buy', candidate.date, candidate.quantity, 0),
          line: candidate.line,
          inferred: true,
          ticker: candidate.ticker,
          productName: candidate.productName,
          assetType: 'fii',
          operation: 'buy',
          date: candidate.date,
          quantity: candidate.quantity,
          unitPrice: 0,
          total: 0,
          duplicate: false,
        });

        next += candidate.quantity;
        index++;
      }
      used.set(movement.ticker, index);
    }

    running.set(movement.ticker, next);
  }

  return { inferred, consumed };
}

/** Rombos que sobraram depois da dedução: aí sim não há explicação no arquivo. */
function findNegativePositions(
  parsed: B3ParsedTrade[],
  existing: ExistingData
): B3NegativePosition[] {
  const tickerById = new Map(
    existing.assets.map((asset) => [asset.id, asset.ticker.toUpperCase()])
  );

  const movements: { ticker: string; date: Date; delta: number }[] = [];
  for (const trade of existing.trades) {
    const ticker = tickerById.get(trade.assetId);
    if (!ticker) continue;
    movements.push({
      ticker,
      date: trade.date,
      delta: trade.operation === 'buy' ? trade.quantity : -trade.quantity,
    });
  }
  for (const trade of parsed) {
    if (trade.duplicate) continue;
    movements.push({
      ticker: trade.ticker,
      date: trade.date,
      delta: trade.operation === 'buy' ? trade.quantity : -trade.quantity,
    });
  }

  movements.sort((a, b) => a.date.getTime() - b.date.getTime());

  const running = new Map<string, number>();
  const worst = new Map<string, B3NegativePosition>();

  for (const movement of movements) {
    const next = (running.get(movement.ticker) ?? 0) + movement.delta;
    running.set(movement.ticker, next);

    if (next < -1e-8) {
      const current = worst.get(movement.ticker);
      if (!current || -next > current.shortfall) {
        worst.set(movement.ticker, {
          ticker: movement.ticker,
          date: movement.date,
          shortfall: -next,
        });
      }
    }
  }

  return [...worst.values()].sort((a, b) => a.ticker.localeCompare(b.ticker));
}

/**
 * Transforma as linhas do relatório no que será gravado, marcando o que já
 * existe. A deduplicação é por contagem: se o arquivo traz duas operações
 * idênticas e o app já tem uma, a segunda entra.
 */
export function buildImportPlan(rows: string[][], existing: ExistingData): B3ImportPlan {
  if (rows.length === 0) throw new B3ImportError('Arquivo vazio.');

  const header = rows[0]!.map((cell) => normalize(cell).trim());
  const indexOf = (label: string) => header.indexOf(normalize(label));

  const columns = {
    direction: indexOf(COLUMNS.direction),
    date: indexOf(COLUMNS.date),
    movement: indexOf(COLUMNS.movement),
    product: indexOf(COLUMNS.product),
    quantity: indexOf(COLUMNS.quantity),
    unitPrice: indexOf(COLUMNS.unitPrice),
    total: indexOf(COLUMNS.total),
  };

  const missing = Object.entries(columns)
    .filter(([, index]) => index < 0)
    .map(([name]) => COLUMNS[name as keyof typeof COLUMNS]);

  if (missing.length > 0) {
    throw new B3ImportError(
      `O arquivo não parece ser o relatório de Movimentação da B3. Colunas faltando: ${missing.join(', ')}.`
    );
  }

  const assetById = new Map(existing.assets.map((asset) => [asset.id, asset]));
  const tickerById = new Map(
    existing.assets.map((asset) => [asset.id, asset.ticker.toUpperCase()])
  );
  const knownTickers = new Set(tickerById.values());

  const existingTrades = countBy(existing.trades, (trade) =>
    tradeKey(
      tickerById.get(trade.assetId) ?? '?',
      trade.operation,
      trade.date,
      trade.quantity,
      trade.unitPrice
    )
  );
  const existingEarnings = countBy(existing.earnings, (earning) =>
    earningKey(
      tickerById.get(earning.assetId) ?? '?',
      earning.kind,
      earning.paymentDate,
      earning.grossValue
    )
  );

  const trades: B3ParsedTrade[] = [];
  const earnings: B3ParsedEarning[] = [];
  const events: B3Event[] = [];
  const skipped: B3SkippedRow[] = [];
  const newAssets = new Map<string, B3NewAsset>();

  for (let i = 1; i < rows.length; i++) {
    const row = rows[i]!;
    const line = i + 1;
    const product = (row[columns.product] ?? '').trim();
    const movement = (row[columns.movement] ?? '').trim();

    if (!product && !movement) continue;

    const skip = (reason: string) => skipped.push({ line, product, movement, reason });

    const date = parseBrDate(row[columns.date] ?? '');
    if (!date) {
      skip('Data inválida');
      continue;
    }

    const { ticker, name } = splitProduct(product);

    const tesouro = isTesouro(product);
    if (!tesouro && !TICKER_PATTERN.test(ticker)) {
      skip('Não é ação, FII nem Tesouro (CDB, LCI ou fundo não listado)');
      continue;
    }
    if (!tesouro && isSubscriptionTicker(ticker)) {
      skip('Direito de subscrição');
      continue;
    }

    const mapping = mapMovement(movement);
    if (mapping.sort === 'skip') {
      skip(mapping.reason);
      continue;
    }

    const quantity = parseNumber(row[columns.quantity] ?? '');
    const unitPrice = parseNumber(row[columns.unitPrice] ?? '');
    const total = parseNumber(row[columns.total] ?? '');

    if (mapping.sort === 'event') {
      events.push({
        line,
        ticker,
        productName: name,
        movement,
        date,
        quantity,
        unitPrice,
        total,
        explanation: mapping.explanation,
      });
      continue;
    }

    // Um ativo já cadastrado manda no tipo; o nome do arquivo decide o resto.
    const existingAsset = existing.assets.find(
      (asset) => asset.ticker.toUpperCase() === ticker
    );
    const assetType = existingAsset?.type ?? (tesouro ? 'tesouro' : detectAssetType(ticker, name));

    if (mapping.sort === 'trade') {
      const direction = normalize(row[columns.direction] ?? '');
      const operation =
        mapping.operation === 'direction'
          ? direction.startsWith('DEB')
            ? 'sell'
            : 'buy'
          : mapping.operation;

      if (quantity <= 0 || unitPrice <= 0) {
        skip('Operação sem quantidade ou preço');
        continue;
      }

      const key = tradeKey(ticker, operation, date, quantity, unitPrice);
      const remaining = existingTrades.get(key) ?? 0;
      const duplicate = remaining > 0;
      if (duplicate) existingTrades.set(key, remaining - 1);

      trades.push({
        key,
        line,
        ticker,
        productName: name,
        assetType,
        operation,
        date,
        quantity,
        unitPrice,
        total: total || quantity * unitPrice,
        duplicate,
      });
    } else if (assetType === 'tesouro') {
      // Renda fixa não paga provento: a remuneração está no preço do título.
      skip('Tesouro Direto não tem provento — a remuneração está no preço');
      continue;
    } else {
      // Provento a débito é estorno ou transferência, nunca renda recebida.
      if (normalize(row[columns.direction] ?? '').startsWith('DEB')) {
        skip('Provento lançado a débito (estorno ou transferência)');
        continue;
      }

      const kind: EarningKind =
        mapping.kind === 'byType'
          ? assetType === 'fii'
            ? 'rendimento_fii'
            : 'dividendo'
          : mapping.kind;

      // No JCP o "Valor da Operação" já vem líquido: o bruto é quantidade x
      // preço, e a diferença é o IRRF de 15%.
      const computedGross = quantity * unitPrice;
      let grossValue = total;
      let irrfWithheld = 0;

      if (kind === 'jcp') {
        grossValue = computedGross > 0 ? computedGross : total;
        irrfWithheld =
          computedGross > 0 && total > 0 && computedGross > total
            ? Number((computedGross - total).toFixed(2))
            : Number((grossValue * JCP_RATE).toFixed(2));
      }

      if (grossValue <= 0) {
        skip('Provento sem valor');
        continue;
      }

      const key = earningKey(ticker, kind, date, grossValue);
      const remaining = existingEarnings.get(key) ?? 0;
      const duplicate = remaining > 0;
      if (duplicate) existingEarnings.set(key, remaining - 1);

      earnings.push({
        key,
        line,
        ticker,
        productName: name,
        assetType,
        kind,
        paymentDate: date,
        quantityBase: quantity,
        valuePerShare: unitPrice,
        grossValue,
        irrfWithheld,
        duplicate,
      });
    }

    if (!knownTickers.has(ticker) && !newAssets.has(ticker)) {
      newAssets.set(ticker, { ticker, name, type: assetType });
    }
  }

  // Ativos que só aparecem em linhas duplicadas já existem; não recadastrar.
  for (const asset of assetById.values()) {
    newAssets.delete(asset.ticker.toUpperCase());
  }

  // Deduz as entradas que faltam antes de reportar rombo: posição negativa
  // é sintoma, não diagnóstico.
  const { inferred, consumed } = inferMissingEntries(trades, events, existing);

  // O tipo do ativo vem do próprio papel, não do chute do FII.
  for (const entry of inferred) {
    const asset = existing.assets.find((a) => a.ticker.toUpperCase() === entry.ticker);
    const fromFile = trades.find((t) => t.ticker === entry.ticker);
    entry.assetType = asset?.type ?? fromFile?.assetType ?? detectAssetType(entry.ticker, entry.productName);
  }

  const allTrades = [...trades, ...inferred].sort(
    (a, b) => a.date.getTime() - b.date.getTime()
  );

  return {
    trades: allTrades,
    earnings,
    newAssets: [...newAssets.values()].sort((a, b) => a.ticker.localeCompare(b.ticker)),
    events: events.filter((event) => !consumed.has(event.line)),
    negativePositions: findNegativePositions(allTrades, existing),
    skipped,
  };
}
